import React, { useContext, useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  List,
  ListItemButton,
  ListItemText,
  ListItemAvatar,
  Avatar,
  CircularProgress,
  Box,
  Chip,
  Typography,
  Divider,
  InputAdornment
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import PersonIcon from '@mui/icons-material/Person';
import axios from 'src/axios';
import { APICONSTANTS } from 'src/components/Constants';
import { useAutoTranslation } from 'src/hooks/useTranslate';
import useDateFormatter from 'src/hooks/useDateFormatter';
import { SnackContext } from 'src/store/ContextStore';

// The recorded sex as a word ('female' -> 'Female'); blank when none is recorded.
const sexLabel = (gender) => {
  const value = String(gender || '').trim();
  return value ? value.charAt(0).toUpperCase() + value.slice(1) : '';
};

// Used by Merge Person and by the lab report inbox's "Search for another patient" (TGCLOUD-1866,
// Phase 3). The inbox compares a pick with what the report prints, so each row shows the date of
// birth (in the deployment's format), the recorded sex, the G-number and the hospital number, and
// a pick also carries `personId` (what a confirmation names), `gender`, `hospitalNumber` and
// `type`. Additive: Merge Person's title, exclusion and the fields it reads are unchanged.
export default function PersonSearchPicker({
  open,
  onClose,
  onSelect,
  excludeFamilyMemberId,
  title = 'Search for person to merge with'
}) {
  const t = useAutoTranslation();
  const fmtDate = useDateFormatter();
  const { setSnack } = useContext(SnackContext);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);

  // Reset state when the dialog opens — previous search residue shouldn't
  // persist across open/close cycles when the parent reuses the component.
  useEffect(() => {
    if (open) {
      setFirstName('');
      setLastName('');
      setResults([]);
      setSearched(false);
    }
  }, [open]);

  const trimmedFirst = firstName.trim();
  const trimmedLast = lastName.trim();
  const canSearch = !!(trimmedFirst || trimmedLast);

  const handleSearch = async () => {
    if (!canSearch) return;
    const body = {};
    if (trimmedFirst) body.firstName = trimmedFirst;
    if (trimmedLast) body.lastName = trimmedLast;

    setLoading(true);
    try {
      const { data } = await axios.post(APICONSTANTS.searchPersons, body);
      const rows = (data?.results || []).filter(
        (r) => r.familyMemberId !== excludeFamilyMemberId
      );
      setResults(rows);
      setSearched(true);
    } catch {
      setSnack({
        status: true,
        msg: t('Search failed. Please try again.'),
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') handleSearch();
  };

  const handleSelect = (row) => {
    onSelect({
      firstName: row.firstName,
      lastName: row.lastName,
      dob: row.dob,
      familyMemberId: row.familyMemberId,
      familyId: row.familyId,
      personId: row.personId,
      gender: row.gender,
      hospitalNumber: row.hospitalNumber,
      type: row.type
    });
    setFirstName('');
    setLastName('');
    setResults([]);
  };

  const renderBadge = (row) => {
    if (row.type === 'proband') {
      return <Chip size="small" color="success" label={t('Proband')} />;
    }
    const label = row.probandName
      ? // TODO: use t('Member of {{name}}', { name: row.probandName }) once useAutoTranslation supports interpolation
        `${t('Member of')} ${row.probandName}`
      : t('Family member');
    return <Chip size="small" label={label} />;
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      slotProps={{
        paper: { sx: { borderRadius: 3, overflow: 'hidden' } }
      }}
    >
      <DialogTitle sx={{ fontWeight: 700, pb: 0.5 }}>{t(title)}</DialogTitle>
      {/* <Typography variant="body2" color="text.secondary" px={3} mb={1}>
        {t('Enter a first or last name to find the person you want to merge.')}
      </Typography> */}
      <DialogContent sx={{ pt: 1 }}>
        <Box
          sx={{
            display: 'flex',
            gap: 1,
            mb: 2,
            mt: 5
          }}
        >
          <TextField
            size="small"
            label={t('First name')}
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            onKeyDown={handleKeyDown}
            autoFocus
            sx={{ flex: 1 }}
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" color="action" />
                  </InputAdornment>
                )
              }
            }}
          />
          <TextField
            size="small"
            label={t('Last name')}
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
            onKeyDown={handleKeyDown}
            sx={{ flex: 1 }}
          />
          <Button
            variant="contained"
            onClick={handleSearch}
            disabled={loading || !canSearch}
            sx={{ textTransform: 'none', px: 3 }}
          >
            {loading ? <CircularProgress size={20} /> : t('Search')}
          </Button>
        </Box>

        {loading && (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              py: 4
            }}
          >
            <CircularProgress size={28} />
          </Box>
        )}

        {!loading && searched && results.length === 0 && (
          <Box
            sx={{
              textAlign: 'center',
              py: 4
            }}
          >
            <Typography
              variant="body2"
              sx={{
                color: 'text.secondary'
              }}
            >
              {t('No results found. Try a different name.')}
            </Typography>
          </Box>
        )}

        {!loading && results.length > 0 && (
          <>
            <Typography
              variant="caption"
              sx={{
                color: 'text.secondary',
                mb: 0.5
              }}
            >
              {results.length === 1
                ? t('{{count}} result', { count: results.length })
                : t('{{count}} results', { count: results.length })}{' '}
              {t('found')}
            </Typography>
            <Divider sx={{ mb: 0.5 }} />
            <List
              dense
              sx={{
                maxHeight: 320,
                overflow: 'auto'
              }}
            >
              {results.map((row) => (
                <ListItemButton
                  key={`${row.type}:${row.familyMemberId}`}
                  onClick={() => handleSelect(row)}
                  sx={{
                    borderRadius: 2,
                    mb: 0.5,
                    '&:hover': { backgroundColor: 'action.hover' }
                  }}
                >
                  <ListItemAvatar>
                    <Avatar
                      sx={{
                        width: 36,
                        height: 36,
                        bgcolor:
                          row.type === 'proband'
                            ? 'success.light'
                            : 'primary.light'
                      }}
                    >
                      <PersonIcon
                        fontSize="small"
                        sx={{
                          color:
                            row.type === 'proband'
                              ? 'success.dark'
                              : 'primary.dark'
                        }}
                      />
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1
                        }}
                      >
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: 600
                          }}
                        >
                          {`${row.firstName || ''} ${
                            row.lastName || ''
                          }`.trim()}
                        </Typography>
                        {renderBadge(row)}
                      </Box>
                    }
                    secondary={
                      <Typography
                        variant="caption"
                        sx={{
                          color: 'text.secondary'
                        }}
                      >
                        {row.familyMemberId} · {t('Family')}: {row.familyId} ·{' '}
                        {t('DOB')}: {fmtDate(row.dob) || '—'} · {t('Sex')}:{' '}
                        {sexLabel(row.gender) || '—'}
                        {row.hospitalNumber
                          ? ` · ${t('Hospital number')}: ${row.hospitalNumber}`
                          : ''}
                      </Typography>
                    }
                  />
                </ListItemButton>
              ))}
            </List>
          </>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} sx={{ textTransform: 'none' }}>
          {t('Cancel')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
